import { useState } from "react";
import axios from "axios";
import {
  ShieldCheck, AlertTriangle, Award, Target, FileQuestion,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PkiQprBox · QPR-2026-Readiness-Check (8 deterministische Regeln + Score).
 *
 * Props:
 *   sisText, planText, berichtText, briefText · String
 *   alleinlebend? · Boolean
 *   customerLabel? · String
 *
 * Verhalten:
 *   - Button startet POST /pflege-ki/qpr-check.
 *   - Zeigt Score (0-100), Severity-Banner, Findings, Fachgespräche (nur PDL/Admin).
 */
export default function PkiQprBox({
  sisText, planText, berichtText, briefText, alleinlebend, customerLabel,
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canCheck = (planText || "").trim().length > 0 || (sisText || "").trim().length > 0;

  const runCheck = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/pflege-ki/qpr-check`, {
        sis_text: sisText || "", plan_text: planText || "",
        bericht_text: berichtText || "", brief_text: briefText || "",
        alleinlebend: !!alleinlebend, customer_label: customerLabel || null,
      }, { withCredentials: true, timeout: 15000 });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="pki-qpr-box"
      className="rounded-2xl border border-cream-300 bg-gradient-to-br from-cream-50 to-saffron/5 p-5 md:p-6 space-y-4"
    >
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Award className="h-5 w-5 text-clay-dark" strokeWidth={2} />
          <div>
            <h3 className="text-[14px] font-bold tracking-[0.04em] uppercase text-clay-dark">
              QPR-2026 · MD-Readiness-Check
            </h3>
            <p className="text-[11.5px] text-sand-600 mt-0.5">
              Pflegefachliche Qualitätsbewertung über alle Dokumente (deterministisch, kein KI-Aufruf).
            </p>
          </div>
        </div>
        <Button
          data-testid="pki-qpr-run"
          onClick={runCheck}
          disabled={!canCheck || busy}
          className="bg-clay-dark hover:bg-clay-dark/90 text-cream"
        >
          {busy ? "Prüfe…" : "QPR prüfen"}
        </Button>
      </header>

      {!canCheck && (
        <p className="text-[12px] text-sand-500 italic">
          Bitte mindestens SIS oder Maßnahmenplan erzeugen.
        </p>
      )}

      {error && (
        <p className="text-[13px] text-brand-darkred" data-testid="pki-qpr-error">{error}</p>
      )}

      {result && <QprResult result={result} />}
    </section>
  );
}

function QprResult({ result }) {
  const sev = result.severity;
  const score = result.score;
  const ringColor = score >= 85
    ? "stroke-sage-dark"
    : score >= 70
      ? "stroke-saffron"
      : score >= 55
        ? "stroke-terra"
        : "stroke-brand-darkred";

  return (
    <div className="space-y-4" data-testid="pki-qpr-result">
      {/* Hero · Score-Ring + Readiness-Label */}
      <div className="rounded-xl border border-cream-300 bg-white p-4 flex flex-col md:flex-row md:items-center gap-5">
        <ScoreRing score={score} colorClass={ringColor} />
        <div className="flex-1 space-y-1">
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-sand-500">
            QPR-Readiness
          </p>
          <p className="text-[15.5px] font-display font-semibold text-sand-800 leading-tight">
            {result.readiness_label}
          </p>
          <p className="text-[12.5px] text-sand-600">
            {result.regeln_erfuellt}/{result.regeln_gesamt} Regeln erfüllt ·
            {" "}{result.findings.length} Hinweise ·
            {" "}{result.fachgespraeche?.length || 0} Fachgespräch-Antworten
            {result.fachgespraeche_hidden && (
              <span className="text-sand-400 italic">{" "}(PDL/Admin only)</span>
            )}
          </p>
        </div>
      </div>

      {/* Findings */}
      {result.findings.length > 0 && (
        <ul className="space-y-2" data-testid="pki-qpr-findings">
          {result.findings.map((f, idx) => (
            <li
              key={idx}
              data-testid={`pki-qpr-finding-${idx}`}
              className={`rounded-lg border p-3 space-y-1.5 ${
                f.severity === "rot"
                  ? "border-brand-darkred/35 bg-brand-darkred/5"
                  : "border-saffron/45 bg-saffron/8"
              }`}
            >
              <div className="flex flex-wrap items-baseline gap-2 text-[11.5px]">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full uppercase font-bold tracking-[0.08em] ${
                  f.severity === "rot" ? "bg-brand-darkred/15 text-brand-darkred" : "bg-saffron/30 text-clay-dark"
                }`}>
                  {f.severity} · −{f.score_impact}P
                </span>
                <span className="font-bold text-sand-700">{f.regel_name}</span>
              </div>
              <p className="text-[13px] text-sand-800 leading-snug">
                <span className="font-semibold">Hinweis:</span> {f.hinweis}
              </p>
              {f.empfehlung && (
                <p className="text-[12.5px] text-sage-dark leading-snug">
                  <span className="font-semibold">Empfehlung:</span> {f.empfehlung}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Fachgespräche (PDL/Admin only) */}
      {result.fachgespraeche?.length > 0 && (
        <div className="rounded-xl border border-clay-dark/25 bg-clay-dark/5 p-4 space-y-3" data-testid="pki-qpr-fachgespraeche">
          <div className="flex items-center gap-2">
            <FileQuestion className="h-4 w-4 text-clay-dark" strokeWidth={2} />
            <h4 className="text-[12.5px] uppercase tracking-[0.14em] font-bold text-clay-dark">
              MD-Fachgespräch · vorbereitete Antworten ({result.fachgespraeche.length})
            </h4>
          </div>
          <ul className="space-y-2.5">
            {result.fachgespraeche.map((fg, idx) => (
              <li key={idx} className="rounded-lg bg-white border border-cream-300 p-3 space-y-1">
                <p className="text-[12px] font-bold text-sand-700">{fg.frage}</p>
                <p className="text-[13px] text-sand-800 italic leading-snug">{fg.antwort}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* COACHING-PLAN bei Score < 70 oder ≥3 Findings */}
      {result.coaching_plan?.anzeigen && (
        <div className="rounded-xl border border-terra/40 bg-terra/8 p-4 space-y-3" data-testid="pki-qpr-coaching">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-terra" strokeWidth={2} />
            <h4 className="text-[12.5px] uppercase tracking-[0.14em] font-bold text-terra">
              {result.coaching_plan.title}
            </h4>
          </div>
          <p className="text-[12.5px] text-sand-700">{result.coaching_plan.summary}</p>
          {result.coaching_plan.gruende?.length > 0 && (
            <div>
              <p className="text-[11.5px] uppercase tracking-[0.12em] font-bold text-sand-600 mb-1.5">
                Gründe für den Score
              </p>
              <ul className="space-y-0.5">
                {result.coaching_plan.gruende.map((g, i) => (
                  <li key={i} className="text-[12.5px] text-sand-800">· {g}</li>
                ))}
              </ul>
            </div>
          )}
          {result.coaching_plan.schritte?.length > 0 && (
            <div>
              <p className="text-[11.5px] uppercase tracking-[0.12em] font-bold text-sand-600 mb-1.5">
                Empfohlene Schritte
              </p>
              <ol className="space-y-2">
                {result.coaching_plan.schritte.map((s, i) => (
                  <li key={i} data-testid={`pki-qpr-coaching-step-${i}`} className="rounded-md bg-white border border-cream-300 p-2.5">
                    <p className="text-[12.5px] font-bold text-clay-dark">
                      {s.prio}. {s.titel}
                    </p>
                    <p className="text-[12px] text-sand-700 leading-snug mt-0.5">
                      {s.beschreibung}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {result.findings.length === 0 && (
        <div className="rounded-xl border border-sage-dark/35 bg-sage-dark/8 p-4 text-[13px] text-sage-dark flex items-center gap-2" data-testid="pki-qpr-clean">
          <ShieldCheck className="h-4 w-4" /> Keine QPR-Hinweise — Dokumentation ist MD-prüfungsbereit.
        </div>
      )}
    </div>
  );
}

function ScoreRing({ score, colorClass }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative w-24 h-24 shrink-0" data-testid="pki-qpr-score-ring">
      <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
        <circle cx="50" cy="50" r={r} className="stroke-cream-300 fill-none" strokeWidth="8" />
        <circle
          cx="50" cy="50" r={r}
          className={`${colorClass} fill-none transition-all duration-700`}
          strokeWidth="8" strokeDasharray={c} strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-display font-bold text-sand-800 leading-none">{score}</span>
        <span className="text-[9.5px] uppercase tracking-[0.16em] text-sand-500 mt-0.5">Score</span>
      </div>
    </div>
  );
}
